import { concat, get } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import InfinityModel from 'ember-infinity/lib/infinity-model';
import t from 'ember-intl/helpers/t';
import PowerSelect from 'ember-power-select/components/power-select';
import and from 'ember-truth-helpers/helpers/and';
import or from 'ember-truth-helpers/helpers/or';
import lowerCase from 'lodash/lowerCase';
import BvLabelled from 'volta/components/bv-labelled';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import BvSelectOptions from './options';
import BvSelectTrigger from './trigger';

import type { ContentValue } from '@glint/template';
import type { IntlService } from 'ember-intl';
import type { PowerSelectArgs, Select } from 'ember-power-select/components/power-select';
import type { IBtnColor, TButtonSize } from 'volta/components/bv-btn';
import type { BvLabelledSignature } from 'volta/components/bv-labelled';
import type { TSize } from 'volta/utils/style-utils';

type ComponentArgs = BvLabelledSignature['Args'] &
  Omit<PowerSelectArgs, 'options' | 'horizontalPosition' | 'verticalPosition'>;

export interface BvSelectArgs extends ComponentArgs {
  id?: string;
  dropdownSize?: TSize;
  // Trigger btn color
  color?: IBtnColor;
  // Trigger btn size
  size?: TButtonSize;
  // Fill container
  fill?: boolean;
  // Trigger btn is subtle ?
  subtle?: boolean;
  // Trigger btn icon before
  iconLeft?: string;
  // Trigger prefix label (Like "Sort by" for example)
  prefix?: ContentValue;
  // Trigger suffix label
  suffix?: ContentValue;
  // If set; get the value from the option with `get`
  bufferSize?: number;
  dataName?: string;
  hideTriggerCaret?: boolean;
  infiniteScroll?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  labelAction?: BvLabelledSignature['Args']['action'];
  labelClass?: string;
  labelInfo?: BvLabelledSignature['Args']['info'];
  labelInline?: BvLabelledSignature['Args']['inline'];
  optionHeight?: number;
  optionKey?: string;
  optionPath?: string;
  plain?: boolean;
  renderAll?: boolean;
  resourceNamePlural?: string;
  focused?: boolean;
  selectedPath?: string;
  tooltipText?: string;
  showBeforeOptions?: boolean;
  options?: any[] | PromiseProxy<any[]> | InfinityModel;
  outline?: boolean;
  verticalPosition?: 'auto' | 'above' | 'below';
  searchField?: string;
  horizontalPosition?:
    | 'auto'
    | 'right'
    | 'left'
    | 'center'
    | 'auto-right'
    | 'auto-left'
    | 'auto-left';
  typeAheadMatcher?: (option: unknown, searchTerm?: string) => void;
}

export interface BvSelectSignature {
  Args: BvSelectArgs;
  Blocks: {
    default: [any, Select];
  };
  Element: HTMLElement;
}

export default class BvSelectComponent extends Component<BvSelectSignature> {
  <template>
    {{!@glint-nocheck}}
    <BvLabelled
      @action={{@labelAction}}
      @dataTestLabelled='bv-select'
      @error={{@error}}
      @fullWidth={{@fill}}
      @helpText={{@helpText}}
      @id={{this.id}}
      @info={{@labelInfo}}
      @inline={{@labelInline}}
      @isEdited={{@isEdited}}
      @isRequired={{@isRequired}}
      @label={{@label}}
      @labelHidden={{@labelHidden}}
      @labelWrapperClass={{@labelWrapperClass}}
      @warning={{@warning}}
      class={{@labelClass}}
      ...attributes
    >
      {{#let
        (component
          BvSelectOptions
          infiniteScroll=@infiniteScroll
          options=this.options
          infiniteModel=(if @infiniteScroll this.options)
          isLoading=@isLoading
          optionHeight=@optionHeight
          optionKey=(orElse @optionKey @optionPath)
          bufferSize=@bufferSize
          renderAll=@renderAll
          extra=@extra
        )
        (if
          @triggerComponent
          (component
            (ensureSafeComponent @triggerComponent this)
            size=@size
            prefix=@prefix
            suffix=@suffix
            disabled=@disabled
            allowClear=@allowClear
          )
          (component
            BvSelectTrigger
            color=@color
            size=@size
            prefix=@prefix
            suffix=@suffix
            fill=@fill
            alignText=(if @fill 'left')
            selectedPath=(or @selectedPath @optionPath)
            subtle=@subtle
            plain=@plain
            iconLeft=@iconLeft
            tooltipText=@tooltipText
            isLoading=@isLoading
            disabled=@disabled
            verticalPosition=@verticalPosition
            hideCaret=@hideTriggerCaret
            allowClear=@allowClear
            outline=@outline
            focused=@focused
          )
        )
        as |OptionsComponent TriggerComponent|
      }}
        <PowerSelect
          @afterOptionsComponent={{@afterOptionsComponent}}
          @allowClear={{@allowClear}}
          @ariaDescribedBy={{@ariaDescribedBy}}
          @ariaInvalid={{@ariaInvalid}}
          @ariaLabel={{@ariaLabel}}
          @ariaLabelledBy={{@ariaLabelledBy}}
          @beforeOptionsComponent={{if
            (orElse @showBeforeOptions true)
            @beforeOptionsComponent
            null
          }}
          @buildSelection={{@buildSelection}}
          @calculatePosition={{@calculatePosition}}
          @closeOnSelect={{@closeOnSelect}}
          @defaultHighlighted={{@defaultHighlighted}}
          @destination={{@destination}}
          @disabled={{@disabled}}
          @dropdownClass={{classNames
            'bv-select__content'
            (if (and @renderInPlace @matchTriggerWidth @fill) 'bv-select__content--fill')
            (if @dropdownSize (concat 'bv-select__content--' @dropdownSize))
            @dropdownClass
          }}
          @eventType={{orElse @eventType 'click'}}
          @extra={{@extra}}
          @groupComponent={{@groupComponent}}
          @highlightOnHover={{@highlightOnHover}}
          @horizontalPosition={{@horizontalPosition}}
          @initiallyOpened={{@initiallyOpened}}
          @loadingMessage={{@loadingMessage}}
          @matcher={{@matcher}}
          @matchTriggerWidth={{if @dropdownSize false @matchTriggerWidth}}
          @noMatchesMessage={{orElse
            @noMatchesMessage
            (t
              'bvList.emptySearchResultTitle'
              resourceNamePlural=(lowerCase (t (or @resourceNamePlural 'items')))
            )
          }}
          @onBlur={{@onBlur}}
          @onChange={{optional @onChange}}
          @onClose={{@onClose}}
          @onFocus={{@onFocus}}
          @onInput={{@onInput}}
          @onKeydown={{@onKeydown}}
          @onOpen={{@onOpen}}
          @options={{this.options}}
          @optionsComponent={{OptionsComponent}}
          @placeholder={{orElse @placeholder (t 'typeToFilter')}}
          @preventScroll={{orElse @preventScroll true}}
          @renderInPlace={{@renderInPlace}}
          @registerAPI={{@registerAPI}}
          @scrollTo={{@scrollTo}}
          @search={{@search}}
          @searchEnabled={{@searchEnabled}}
          @searchField={{or @searchField @optionPath}}
          @searchMessage={{@searchMessage}}
          @searchPlaceholder={{@searchPlaceholder}}
          @selected={{@selected}}
          @selectedItemComponent={{@selectedItemComponent}}
          @triggerClass={{classNames 'bv-select' (if @fill 'bv-select--fill') @triggerClass}}
          @triggerComponent={{ensureSafeComponent TriggerComponent this}}
          @triggerId={{@triggerId}}
          @triggerRole={{null}}
          @typeAheadMatcher={{@typeAheadMatcher}}
          @verticalPosition={{@verticalPosition}}
          @tabindex={{@tabindex}}
          data-test-bv-select-name={{@dataName}}
          autocomplete='on'
          as |option term|
        >
          {{#if (has-block)}}
            {{yield option term}}
          {{else}}
            {{if @optionPath (get option @optionPath) option}}
          {{/if}}
        </PowerSelect>
      {{/let}}
    </BvLabelled>
  </template>
  @service intl!: IntlService;

  id = this.args.id ?? `BvSelect-${guidFor(this)}`;

  get inifinityModel() {
    return this.args.options instanceof InfinityModel ? this.args.options : undefined;
  }

  get options() {
    return this.args.options as any[] | PromiseProxy<any[]> | undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelect: typeof BvSelectComponent;
    'bv-select': typeof BvSelectComponent;
  }
}
